/**
 * Monophonic pitch detection (YIN, de Cheveigné & Kawahara 2002) for the
 * tuner (F-PRAC-02). Pure: takes samples, returns frequency and clarity.
 */
import { midiToName } from "../theory/notes";

export interface PitchOptions { minHz?: number; maxHz?: number; threshold?: number; minRms?: number; }
export interface PitchResult { frequency: number; clarity: number; }

export function detectPitch(x: Float32Array, sampleRate: number, opts: PitchOptions = {}): PitchResult | null {
  const { minHz = 40, maxHz = 1600, threshold = 0.15, minRms = 0.01 } = opts;
  let energy = 0;
  for (let i = 0; i < x.length; i++) energy += x[i]! * x[i]!;
  if (Math.sqrt(energy / x.length) < minRms) return null; // too quiet to judge

  const maxTau = Math.min(Math.floor(sampleRate / minHz), Math.floor(x.length / 2));
  const minTau = Math.max(2, Math.floor(sampleRate / maxHz));
  const w = x.length - maxTau;
  const diffs = new Float64Array(maxTau + 1);
  const cmnd = new Float32Array(maxTau + 1);
  cmnd[0] = 1;
  let running = 0;
  for (let tau = 1; tau <= maxTau; tau++) {
    let d = 0;
    for (let i = 0; i < w; i++) {
      const diff = x[i]! - x[i + tau]!;
      d += diff * diff;
    }
    diffs[tau] = d;
    running += d;
    cmnd[tau] = running > 0 ? (d * tau) / running : 1; // cumulative mean normalised difference
  }

  // First dip below the threshold, followed down to its local minimum.
  let tau = -1;
  for (let t = minTau; t <= maxTau; t++) {
    if (cmnd[t]! < threshold) {
      while (t + 1 <= maxTau && cmnd[t + 1]! < cmnd[t]!) t++;
      tau = t;
      break;
    }
  }
  if (tau < 0) return null; // no clear periodicity (noise, chords)

  // Sub-sample refinement. For short periods, measure k whole periods (the
  // difference function also dips at k·tau) so interpolation error is divided by k.
  const refine = (t: number): number => {
    if (t <= 1 || t >= maxTau) return t;
    const a = diffs[t - 1]!, b = diffs[t]!, c = diffs[t + 1]!;
    const denom = a - 2 * b + c;
    return denom > 0 ? t + (a - c) / (2 * denom) : t;
  };
  const period = refine(tau);
  const k = Math.max(1, Math.min(8, Math.floor((maxTau - 2) / period)));
  if (k === 1) return { frequency: sampleRate / period, clarity: 1 - cmnd[tau]! };
  // Snap to the local minimum nearest k·period, then interpolate there.
  let center = Math.round(k * period);
  for (let t = Math.max(2, center - 2); t <= Math.min(maxTau - 1, center + 2); t++) if (diffs[t]! < diffs[center]!) center = t;
  return { frequency: (sampleRate * k) / refine(center), clarity: 1 - cmnd[tau]! };
}

export interface NoteReading { midi: number; name: string; cents: number; frequency: number; }

/** Nearest equal-tempered note and deviation in cents (−50…+50). */
export function noteFromFrequency(frequency: number, a4Hz = 440): NoteReading {
  const exact = 69 + 12 * Math.log2(frequency / a4Hz);
  const midi = Math.round(exact);
  return { midi, name: midiToName(midi), cents: Math.round((exact - midi) * 1000) / 10, frequency };
}
